import { Button, Input, Select } from '@napaicp/ui'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CompetitionCard } from '@/components/competitions/competition-card'
import {
  usePublicCompetitions,
  usePublicDiscoveryCategories,
  type PublicCompetitionListFilters,
  type PublicCompetitionSummary,
} from '@/features/public/api'
import { competitionStatusLabel } from '@/lib/labels'

/** The nearest date that hasn't passed yet, or null if every date is past or unset. */
function nextUpcomingDate(competition: PublicCompetitionSummary): string | null {
  const candidates = [
    competition.registrationStartAt,
    competition.registrationEndAt,
    competition.submissionStartAt,
    competition.submissionEndAt,
    competition.resultPublishAt,
  ].filter((value): value is string => value !== null && new Date(value) > new Date())
  if (candidates.length === 0) return null
  return candidates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0] ?? null
}

const PAGE_SIZE = 6
const STATUS_FILTER_VALUES: NonNullable<PublicCompetitionListFilters['status']>[] = [
  'published',
  'registration_open',
  'submission_open',
  'judging',
  'completed',
]

/**
 * The first thing an anonymous visitor sees proof of: real, currently public
 * competitions, not marketing copy. Skeleton while loading, an honest empty
 * state at zero (never hidden — a visitor should be able to tell the
 * platform is live even when nothing is public right now), a card grid
 * otherwise.
 *
 * PR-128/API-067 (v3.14 → this delta): search/status/participation-type/
 * closing-soon/sort/pagination controls wired to the query API added in
 * v3.14. `q` is debounced client-side (300ms) so fast typing does not fire
 * a request per keystroke; every other control commits immediately. Any
 * filter change resets `page` back to 0 — otherwise a narrower result set
 * could leave the visitor stranded on a now-empty page.
 *
 * TASK-U-007 (Wave U Phase 2): cards render through the shared
 * `CompetitionCard` template (PR-051/UX-046) — BR-098's slug/URL-hiding rule
 * was already satisfied here (only `competition.slug` was used to build the
 * navigation `href`, never printed as text), and that stays true through the
 * shared component, which only accepts an `href`, never a slug to display.
 */
export function PublishedCompetitionsSection() {
  const { t } = useTranslation('home')

  const [qInput, setQInput] = useState('')
  const [q, setQ] = useState('')
  const [status, setStatus] = useState<PublicCompetitionListFilters['status'] | ''>('')
  const [participationType, setParticipationType] = useState<
    PublicCompetitionListFilters['participationType'] | ''
  >('')
  const [closingSoon, setClosingSoon] = useState(false)
  const [category, setCategory] = useState('')
  const [sort, setSort] = useState<PublicCompetitionListFilters['sort']>('newest')
  const [page, setPage] = useState(0)

  const discoveryCategories = usePublicDiscoveryCategories()

  useEffect(() => {
    const timeout = setTimeout(() => {
      setQ(qInput.trim())
      setPage(0)
    }, 300)
    return () => clearTimeout(timeout)
  }, [qInput])

  const query = usePublicCompetitions({
    q: q.length > 0 ? q : undefined,
    status: status === '' ? undefined : status,
    participationType: participationType === '' ? undefined : participationType,
    closingSoon: closingSoon ? true : undefined,
    category: category === '' ? undefined : category,
    sort,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  })
  const competitions = query.data?.competitions ?? []
  const total = query.data?.total ?? 0
  const hasFilters =
    q.length > 0 ||
    status !== '' ||
    participationType !== '' ||
    closingSoon ||
    category !== '' ||
    sort !== 'newest'

  function resetPageAnd<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value)
      setPage(0)
    }
  }

  return (
    <section id="published-competitions" className="flex flex-col gap-4 pt-1 pb-4 md:pt-2 md:pb-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
          {t('publishedCompetitions.heading')}
        </h2>
        <p className="text-lg text-muted-foreground">{t('publishedCompetitions.description')}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={qInput}
          onChange={(event) => setQInput(event.target.value)}
          placeholder={t('publishedCompetitions.filters.searchPlaceholder')}
          aria-label={t('publishedCompetitions.filters.searchPlaceholder')}
          className="w-full sm:w-56"
        />
        <Select
          value={status}
          onChange={(event) =>
            resetPageAnd(setStatus)(
              event.target.value as PublicCompetitionListFilters['status'] | '',
            )
          }
          aria-label={t('publishedCompetitions.filters.statusLabel')}
          className="w-auto"
        >
          <option value="">{t('publishedCompetitions.filters.statusAny')}</option>
          {STATUS_FILTER_VALUES.map((value) => (
            <option key={value} value={value}>
              {competitionStatusLabel(t, value)}
            </option>
          ))}
        </Select>
        <Select
          value={participationType}
          onChange={(event) =>
            resetPageAnd(setParticipationType)(
              event.target.value as PublicCompetitionListFilters['participationType'] | '',
            )
          }
          aria-label={t('publishedCompetitions.filters.participationLabel')}
          className="w-auto"
        >
          <option value="">{t('publishedCompetitions.filters.participationAny')}</option>
          <option value="individual">{t('publishedCompetitions.filters.individual')}</option>
          <option value="team">{t('publishedCompetitions.filters.team')}</option>
        </Select>
        {discoveryCategories.data !== undefined && discoveryCategories.data.length > 0 ? (
          <Select
            value={category}
            onChange={(event) => resetPageAnd(setCategory)(event.target.value)}
            aria-label={t('publishedCompetitions.filters.categoryLabel')}
            className="w-auto"
          >
            <option value="">{t('publishedCompetitions.filters.categoryAny')}</option>
            {discoveryCategories.data.map((entry) => (
              <option key={entry.slug} value={entry.slug}>
                {entry.name}
              </option>
            ))}
          </Select>
        ) : null}
        <Select
          value={sort}
          onChange={(event) =>
            resetPageAnd(setSort)(event.target.value as PublicCompetitionListFilters['sort'])
          }
          aria-label={t('publishedCompetitions.filters.sortLabel')}
          className="w-auto"
        >
          <option value="newest">{t('publishedCompetitions.filters.sortNewest')}</option>
          <option value="deadline">{t('publishedCompetitions.filters.sortDeadline')}</option>
        </Select>
        <label className="flex items-center gap-1.5 text-sm">
          <input
            type="checkbox"
            checked={closingSoon}
            onChange={(event) => resetPageAnd(setClosingSoon)(event.target.checked)}
          />
          {t('publishedCompetitions.filters.closingSoon')}
        </label>
      </div>

      {query.isPending ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              aria-hidden="true"
              className="h-40 animate-pulse rounded-xl border border-border bg-muted"
            />
          ))}
        </div>
      ) : competitions.length === 0 ? (
        <p className="text-lg text-muted-foreground">
          {hasFilters
            ? t('publishedCompetitions.filters.noMatches')
            : t('publishedCompetitions.empty')}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {competitions.map((competition) => {
              const upcoming = nextUpcomingDate(competition)
              return (
                <CompetitionCard
                  key={competition.slug}
                  href={`/c/${competition.slug}`}
                  status={competition.status}
                  name={competition.name}
                  summary={competition.shortDescription}
                  keyDateValue={
                    upcoming !== null
                      ? t('publishedCompetitions.nextDate', {
                          date: new Date(upcoming).toLocaleDateString(),
                        })
                      : null
                  }
                  ctaLabel={t('publishedCompetitions.viewDetails')}
                  officialWebsiteUrl={competition.officialWebsiteUrl}
                  officialWebsiteLabel={t('common:competition.officialWebsite')}
                  officialWebsiteAriaLabel={t('common:competition.officialWebsiteAriaLabel', {
                    name: competition.name,
                  })}
                  logoUrl={competition.logo?.url}
                  t={t}
                />
              )
            })}
          </div>
          {total > PAGE_SIZE ? (
            <div className="flex items-center justify-center gap-3">
              <Button
                variant="secondary"
                disabled={page === 0}
                onClick={() => setPage((current) => Math.max(0, current - 1))}
              >
                {t('publishedCompetitions.filters.previousPage')}
              </Button>
              <span className="text-sm text-muted-foreground">
                {t('publishedCompetitions.filters.pageOf', {
                  page: page + 1,
                  totalPages: Math.ceil(total / PAGE_SIZE),
                })}
              </span>
              <Button
                variant="secondary"
                disabled={(page + 1) * PAGE_SIZE >= total}
                onClick={() => setPage((current) => current + 1)}
              >
                {t('publishedCompetitions.filters.nextPage')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </section>
  )
}
